import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import isEmpty from 'lodash/isEmpty'

import Info from 'components/Info'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import { Fields as RedeemTemplate } from 'models/RedeemTemplate'
import { generateTypeItem, getInStoreValue } from 'scenes/RedeemManager/helpers'

type DynamicCouponsInfoProps = {
  template?: RedeemTemplate
  isDirect?: boolean
}

const t = buildTranslate('redeem_manager.detail.cards.instore_redemption')

const DynamicCouponsInfo = ({
  template,
  isDirect = false,
}: DynamicCouponsInfoProps) => {
  const freeModifierRewardsEnabled = useFlag('mx-free-modifier-rewards', false)

  if (!template) return null

  const typeItem = generateTypeItem(template)
  const isModifier = typeItem?.subtype === 'modifier'
  const instoreTemplateProductIds = template?.product_ids?.instore
  const isFreeItem = typeItem?.types.includes('manual/item')
  const inStoreValues = template?.product_ids?.instore

  const requiredProductIds = getInStoreValue(
    inStoreValues,
    instoreTemplateProductIds?.required
  )

  const getDiscountedProductsLabel = () => {
    if (isModifier) return t('pos_free_modifier_item_ids')
    if (isFreeItem) return t('coupon_codes.pos_free_product_ids')
    return t('coupon_codes.pos_discounted_product_ids')
  }

  const getDiscountedModifiersLabel = () => {
    if (isModifier) return t('pos_free_modifier_ids')
    if (isFreeItem) return t('coupon_codes.pos_free_item_modifier_ids')
    return t('coupon_codes.pos_discounted_modifier_ids')
  }

  const discountedProductIds = isModifier
    ? getInStoreValue(
        inStoreValues,
        instoreTemplateProductIds?.discounted_related,
        'discounted_related'
      )
    : getInStoreValue(
        inStoreValues,
        instoreTemplateProductIds?.discounted,
        'discounted'
      )
  const discountedModifierIds = isModifier
    ? getInStoreValue(
        inStoreValues,
        instoreTemplateProductIds?.discounted,
        'discounted'
      )
    : getInStoreValue(
        inStoreValues,
        instoreTemplateProductIds?.discounted_related,
        'discounted_related'
      )

  const modifierDiscountIds = getInStoreValue(
    inStoreValues,
    undefined,
    'discounted_modifiers'
  )

  const displayRequiredProducts = !isEmpty(requiredProductIds)
  const displayDiscountedProducts = !isEmpty(discountedProductIds)
  const displayDiscountedModifierIds = !isEmpty(discountedModifierIds)
  const displayModifierDiscountIds = !isEmpty(modifierDiscountIds)

  return (
    <>
      <Info label={t('redemption')} value={t('coupon_codes.enforced_by_pos')} />
      <Info
        className="mt-s"
        label={t('coupon_codes.type')}
        value={
          <>
            <Text.BodyText3 color="grey70">
              {t('coupon_codes.integration')}
            </Text.BodyText3>
            <Text.BodyText4 color="grey70" tag="div" className="mt-xs">
              <span
                dangerouslySetInnerHTML={{
                  __html: isDirect
                    ? t('coupon_codes.entered_by_customer')
                    : t('coupon_codes.issued_5_digit_code'),
                }}
              />
            </Text.BodyText4>
          </>
        }
      />

      <Switch condition={template.subtype !== 'purchase'}>
        <Switch condition={displayRequiredProducts}>
          <Info
            className="mt-m"
            label={t('coupon_codes.pos_required_product_ids')}
            value={
              <Text.BodyText3 color="grey70">
                {requiredProductIds?.join(', ')}
              </Text.BodyText3>
            }
          />
        </Switch>
        <Switch condition={displayDiscountedProducts}>
          <Info
            className="mt-m"
            label={getDiscountedProductsLabel()}
            value={
              <Text.BodyText3 color="grey70">
                {discountedProductIds?.join(', ')}
              </Text.BodyText3>
            }
          />
        </Switch>
        <Switch condition={freeModifierRewardsEnabled}>
          <Info
            className="mt-m"
            label={getDiscountedModifiersLabel()}
            value={
              <Text.BodyText3 color="grey70">
                <Switch
                  condition={displayDiscountedModifierIds}
                  fallback={
                    <p className="grey-70">
                      {t('coupon_codes.pos_no_modifiers')}
                    </p>
                  }
                >
                  {discountedModifierIds?.join(', ')}
                </Switch>
              </Text.BodyText3>
            }
          />
        </Switch>
        <Switch condition={displayModifierDiscountIds}>
          <Info
            className="mt-m"
            label={t('coupon_codes.pos_modifier_discount_ids')}
            value={
              <Text.BodyText3 color="grey70">
                {modifierDiscountIds.join(', ')}
              </Text.BodyText3>
            }
          />
        </Switch>
      </Switch>

      <Text.BodyText3 className="mb-4 mt-l" color="grey70">
        <Switch condition={isModifier}>
          {t('coupon_codes.discounted_modifier_text')}
        </Switch>
        <Switch condition={!isModifier}>
          {t('coupon_codes.discounted_item_modifier_text')}
        </Switch>
      </Text.BodyText3>
    </>
  )
}

export default DynamicCouponsInfo
